/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CarCard.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState, memo } from "react";
import VlcCar from "../../assets/carForVLC.png";
import customConsole from "../../config/customConsole";
import { shallowEqual, useSelector } from "react-redux";
import { vlcComponentStyle } from "../../components/customComponentsV2/vlc/config/vlcConfig";

const ZoneDetailsBox = memo(function ZoneDetailsBox({
  zonePositionName,
  eeComponentsCount,
  vehicleSystemsCount,
  isSelected,
  showVehicleView,
  onSelect,
}) {
  const count = showVehicleView
    ? (vehicleSystemsCount ?? 0)
    : (eeComponentsCount ?? 0);

  return (
    <Box
      sx={{
        width: "120px",
        minWidth: "120px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
          cursor: "pointer",
        },
        border: isSelected ? "2px solid #9cd5f5" : "1px solid transparent",
        borderRadius: 0,
        height: "40px",
        boxShadow: "0 6px 10px rgba(0, 0, 0, 0.2)",
        backgroundColor: "#fff",
        px: 1,
      }}
      onClick={onSelect}
    >
      <Typography
        sx={{
          fontSize: { xs: "9px", sm: "10px", md: "11px" },
          fontWeight: 500,
          color: "#6B7280",
          textAlign: "center",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {`${zonePositionName} ${count}`}
      </Typography>
    </Box>
  );
});

ZoneDetailsBox.propTypes = {
  zonePositionName: PropTypes.string,
  eeComponentsCount: PropTypes.number,
  vehicleSystemsCount: PropTypes.number,
  isSelected: PropTypes.bool,
  showVehicleView: PropTypes.bool,
  onSelect: PropTypes.func,
};

const DummyComponent = memo(function DummyComponent() {
  return (
    <Box
      sx={{
        height: "40px",
        minWidth: "120px",
        width: "120px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
      }}
    />
  );
});

function VlcCarCard({
  availableZones,
  selectedComponent,
  setSelectedComponent,
  zoneEECountList,
  zoneVSCountList,
  selectedVehicleView,
  centralizedEEList,
  selectedEEComponentDetails,
  vsAndVCOptionsList,
  selectedSingleVariantDetails,
  viewArchived,
}) {
  customConsole("----------- zoneVSCountList -----------");
  customConsole({ zoneVSCountList });
  const vehicleSystems = useSelector(
    (state) => state.labCarReducer.vehicleSystems,
  );
  const { swModulesList } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );

  const vehicleComponents = useSelector(
    (state) => state.labCarReducer.vehicleComponents,
  );
  const [showVehicleView, setShowVehicleView] = useState(false);

  useEffect(() => {
    setShowVehicleView(selectedVehicleView === "VEHICLE");
  }, [selectedVehicleView]);
  useEffect(() => {
    if (
      availableZones &&
      availableZones.length > 0 &&
      !selectedComponent?.zone_position_name
    ) {
      setSelectedComponent({
        zone_position_name: availableZones[0].position_name,
      });
    }
  }, [availableZones, selectedComponent?.zone_position_name, setSelectedComponent]);

  useEffect(() => {
    customConsole("selectedComponent changed:", selectedComponent);
  }, [selectedComponent]);

  const getCount = (list) => {
    if (Array.isArray(list)) return list.length;
    if (Array.isArray(list?.data)) return list.data.length;
    if (typeof list?.count === "number") return list.count;
    if (typeof list?.total_count === "number") return list.total_count;
    if (typeof list?.total === "number") return list.total;
    return 0;
  };
  const hasVariant = Boolean(selectedSingleVariantDetails?.variant_id);
  const centralizedEECount = useMemo(
    () => (hasVariant ? getCount(centralizedEEList) : 0),
    [centralizedEEList, hasVariant],
  );
  const vehicleSystemsCount = useMemo(
    () => (hasVariant ? getCount(vehicleSystems) : 0),
    [vehicleSystems, hasVariant],
  );
  const vehicleComponentsCount = useMemo(
    () => (hasVariant ? getCount(vehicleComponents) : 0),
    [vehicleComponents, hasVariant],
  );
  const swModulesCount = useMemo(
    () => (hasVariant ? getCount(swModulesList) : 0),
    [swModulesList, hasVariant],
  );

  const isZonesEmpty =
    Array.isArray(availableZones) && availableZones.length === 0;

  const zonesByPosition = useMemo(() => {
    const map = new Map();
    if (Array.isArray(availableZones)) {
      for (const zone of availableZones) {
        if (zone?.zone_position) {
          map.set(zone.zone_position, zone);
        }
      }
    }
    return map;
  }, [availableZones]);

  const eeCountsByPosition = useMemo(() => {
    const map = new Map();
    if (Array.isArray(zoneEECountList)) {
      for (const zone of zoneEECountList) {
        if (zone?.zone_position) {
          map.set(zone.zone_position, zone.ee_components_count ?? 0);
        }
      }
    }
    return map;
  }, [zoneEECountList]);

  const vsCountsByPosition = useMemo(() => {
    const map = new Map();
    if (Array.isArray(zoneVSCountList)) {
      for (const zone of zoneVSCountList) {
        if (zone?.zone_position) {
          map.set(zone.zone_position, zone.vehicle_systems_count ?? 0);
        }
      }
    }
    return map;
  }, [zoneVSCountList]);

  const renderZoneSlot = (position, emptyFallback = "null") => {
    const zone = zonesByPosition.get(position);
    if (!zone) {
      if (isZonesEmpty) {
        return emptyFallback === "dummy" ? <DummyComponent /> : null;
      }
      return <DummyComponent />;
    }

    const eeCount = eeCountsByPosition.get(position);
    const vsCount = vsCountsByPosition.get(position);
    const handleSelect = () => {
      setSelectedComponent({
        zone_position_name: zone.position_name,
        ee_components_count: eeCount ?? 0,
        vehicle_systems_count: vsCount ?? 0,
      });
    };

    return (
      <Box>
        <ZoneDetailsBox
          zonePositionName={zone.position_name}
          eeComponentsCount={eeCount}
          vehicleSystemsCount={vsCount}
          isSelected={
            selectedComponent?.zone_position_name === zone.position_name
          }
          showVehicleView={showVehicleView}
          onSelect={handleSelect}
        />
      </Box>
    );
  };
  return (
    <Box elevation={12} sx={styles.mainContainer}>
      <Box
        sx={{
          ...styles.frontZonesContainer,
          ...(selectedVehicleView === "ECU" && isZonesEmpty
            ? { justifyContent: "center" }
            : {}),
        }}
      >
        {renderZoneSlot("FRONT-LEFT")}
        {renderZoneSlot("FRONT")}

        {selectedVehicleView === "VEHICLE" && isZonesEmpty && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 3,
              p: 2,
            }}
          >
            <Box
              sx={{
                width: "fit-content",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  cursor: "pointer",
                },
                border: "1px solid transparent",
                borderRadius: 0,
                height: "50px",
                boxShadow: "0 6px 10px rgba(0, 0, 0, 0.2)",
                backgroundColor: "#fff",
                px: 1,
              }}
            >
              <Typography sx={vlcComponentStyle.carCard.zoneText}>
                {`Vehicle Systems ${vehicleSystemsCount}`}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "fit-content",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  cursor: "pointer",
                },
                border: "1px solid transparent",
                borderRadius: 0,
                height: "50px",
                boxShadow: "0 6px 10px rgba(0, 0, 0, 0.2)",
                backgroundColor: "#fff",
                px: 1,
              }}
            >
              <Typography sx={vlcComponentStyle.carCard.zoneText}>
                {`Vehicle Components ${vehicleComponentsCount}`}
              </Typography>
            </Box>
          </Box>
        )}

        {selectedVehicleView === "ECU" && isZonesEmpty && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 3,
              p: 2,
            }}
          >
            <Box
              sx={{
                width: "fit-content",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  cursor: "pointer",
                },
                border: "1px solid transparent",
                borderRadius: 0,
                height: "50px",
                boxShadow: "0 6px 10px rgba(0, 0, 0, 0.2)",
                backgroundColor: "#fff",
                px: 1,
              }}
            >
              <Typography sx={vlcComponentStyle.carCard.zoneText}>
                {`EE  Components ${centralizedEECount}`}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "fit-content",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  cursor: "pointer",
                },
                border: "1px solid transparent",
                borderRadius: 0,
                height: "50px",
                boxShadow: "0 6px 10px rgba(0, 0, 0, 0.2)",
                backgroundColor: "#fff",
                px: 1,
              }}
            >
              <Typography sx={vlcComponentStyle.carCard.zoneText}>
                {`Software Modules ${swModulesCount}`}
              </Typography>
            </Box>
          </Box>
        )}

        {renderZoneSlot("FRONT-RIGHT")}
      </Box>
      <Box sx={{ ...styles.middleZonesContainer }}>
        {renderZoneSlot("LEFT", "dummy")}
        <Box
          sx={{
            height: { xs: "240px", sm: "260px", md: "280px" },
            width: { xs: "150px", sm: "150px", md: "150px" },
            flexShrink: 0,
            backgroundImage: `url(${VlcCar})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: "0.8",
          }}
        />
        {renderZoneSlot("RIGHT", "dummy")}
      </Box>
      <Box sx={styles.rearZonesContainer}>
        {renderZoneSlot("REAR-LEFT")}
        {renderZoneSlot("REAR")}
        {renderZoneSlot("REAR-RIGHT")}
      </Box>
    </Box>
  );
}

export default memo(VlcCarCard);

VlcCarCard.propTypes = {
  availableZones: PropTypes.arrayOf(
    PropTypes.shape({
      zone_position: PropTypes.string,
      position_name: PropTypes.string,
    }),
  ),
  selectedComponent: PropTypes.shape({
    zone_position_name: PropTypes.string,
  }),
  setSelectedComponent: PropTypes.func,
  zoneEECountList: PropTypes.arrayOf(
    PropTypes.shape({
      zone_position: PropTypes.string,
      ee_components_count: PropTypes.number,
    }),
  ),
  zoneVSCountList: PropTypes.arrayOf(
    PropTypes.shape({
      zone_position: PropTypes.string,
      vehicle_systems_count: PropTypes.number,
    }),
  ),
  selectedVehicleView: PropTypes.string,
  centralizedEEList: PropTypes.arrayOf(PropTypes.object),
  selectedEEComponentDetails: PropTypes.object,
  vsAndVCOptionsList: PropTypes.arrayOf(PropTypes.object),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  viewArchived: PropTypes.bool,
};

const styles = {
  mainContainer: {
    flex: "0 0 auto",
    minWidth: 0,
    width: "100%",
    height: "450px",
    minHeight: "450px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: { xs: "2px", sm: "4px", md: "6px" },
    padding: {
      xs: "2px 2px 2px 2px",
      sm: "4px 4px 4px 4px",
      md: "6px 6px 6px 6px",
    },
    boxSizing: "border-box",
  },
  frontZonesContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    height: "50px",
  },
  middleZonesContainer: {
    display: "grid",
    gridTemplateColumns: "120px 150px 120px",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: "100%",
    columnGap: { xs: "0px", sm: "0px", md: "0px" },
    rowGap: 0,
    height: { xs: "300px", sm: "300px", md: "300px" },
  },
  rearZonesContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    height: "50px",
  },
  zoneContainer: {
    px: "2px",
    borderRadius: "20%",
    height: { xs: "20px", sm: "22px", md: "24px" },
    width: { xs: "28px", sm: "30px", md: "32px" },
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    "&:hover": {
      cursor: "pointer",
      background: "#9aeb75",
    },
    background: "#37b518",
    opacity: "0.6",
    boxSizing: "border-box",
  },
  zoneText: {
    fontSize: { xs: "4px", sm: "5px", md: "6px" },
    marginTop: "0px",
    color: "white",
  },
  dummyComp: {
    height: { xs: "20px", sm: "22px", md: "24px" },
    width: { xs: "28px", sm: "30px", md: "32px" },
  },
};